import React, { useEffect, useRef, useState } from 'react';
import SharedNav from '@/components/SharedNav';
import {
  Box, Camera, AlertTriangle, Clock, Ruler, Upload, Layers,
  CheckCircle2, Loader2, XCircle, Download, Eye, Sprout, BookOpen,
} from 'lucide-react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

const API_BASE = (typeof window !== 'undefined' && window.location && window.location.origin) || process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_BASE}/api/reconstruction-3d`;

// ──────────────────────────────────────────────────────────────
// Ascii PLY parser — sparse point cloud loader
// ──────────────────────────────────────────────────────────────
async function parseAsciiPLY(arrayBuffer) {
  const dv = new DataView(arrayBuffer);
  const decoder = new TextDecoder('ascii');
  // Find end_header
  const text = decoder.decode(arrayBuffer);
  const headerEnd = text.indexOf('end_header');
  if (headerEnd < 0) throw new Error('Not a PLY file');
  const header = text.slice(0, headerEnd);
  let nVertex = 0;
  let isBinary = false;
  header.split('\n').forEach((line) => {
    if (line.startsWith('element vertex')) {
      nVertex = parseInt(line.split(/\s+/).pop(), 10);
    }
    if (line.startsWith('format')) {
      isBinary = line.includes('binary');
    }
  });
  if (isBinary) {
    throw new Error('Binary PLY not supported in viewer (server always returns ASCII)');
  }
  const bodyStart = text.indexOf('\n', headerEnd + 'end_header'.length) + 1;
  const body = text.slice(bodyStart);
  const positions = new Float32Array(nVertex * 3);
  const colors = new Float32Array(nVertex * 3);
  let idx = 0;
  let lineIdx = 0;
  const lines = body.split('\n');
  for (let i = 0; i < lines.length && lineIdx < nVertex; i++) {
    const tok = lines[i].trim().split(/\s+/);
    if (tok.length < 3) continue;
    positions[idx] = parseFloat(tok[0]);
    positions[idx + 1] = parseFloat(tok[1]);
    positions[idx + 2] = parseFloat(tok[2]);
    if (tok.length >= 6) {
      colors[idx] = parseInt(tok[3], 10) / 255;
      colors[idx + 1] = parseInt(tok[4], 10) / 255;
      colors[idx + 2] = parseInt(tok[5], 10) / 255;
    } else {
      colors[idx] = colors[idx + 1] = colors[idx + 2] = 0.7;
    }
    idx += 3;
    lineIdx += 1;
  }
  return { positions: positions.subarray(0, idx), colors: colors.subarray(0, idx), count: lineIdx };
}

// ──────────────────────────────────────────────────────────────
// Three.js point-cloud viewer
// ──────────────────────────────────────────────────────────────
function PointCloud({ positions, colors }) {
  const ref = useRef();
  useEffect(() => {
    if (!ref.current) return;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    g.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    g.computeBoundingSphere();
    ref.current.geometry = g;
  }, [positions, colors]);
  return (
    <points ref={ref}>
      <bufferGeometry />
      <pointsMaterial size={0.02} vertexColors sizeAttenuation />
    </points>
  );
}

function PointCloudViewer({ plyUrl }) {
  const [pc, setPc] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!plyUrl) return;
    fetch(plyUrl, { credentials: 'include' })
      .then(r => r.arrayBuffer())
      .then(parseAsciiPLY)
      .then(setPc)
      .catch(e => setErr(e.message));
  }, [plyUrl]);

  if (err) {
    return <div className="text-sm text-red-700 p-4" data-testid="recon3d-viewer-error">{err}</div>;
  }
  if (!pc) {
    return <div className="flex items-center justify-center h-full text-sm"
                style={{ color: '#4F4B44' }} data-testid="recon3d-viewer-loading">
      <Loader2 size={14} className="animate-spin mr-2" /> Loading point cloud…
    </div>;
  }
  return (
    <Canvas camera={{ position: [3, 3, 3], fov: 50 }} data-testid="recon3d-viewer-canvas">
      <ambientLight intensity={0.6} />
      <PointCloud positions={pc.positions} colors={pc.colors} />
      <OrbitControls enablePan enableZoom enableRotate makeDefault />
      <gridHelper args={[10, 20, '#B5BAB2', '#E5E5E0']} />
      <axesHelper args={[1]} />
    </Canvas>
  );
}

// ──────────────────────────────────────────────────────────────
// Upload sub-tab
// ──────────────────────────────────────────────────────────────
function UploadPanel({ onSubmit, submitting, error }) {
  const [files, setFiles] = useState([]);
  const [markerCm, setMarkerCm] = useState(5);
  const handleFiles = (fl) => {
    const arr = Array.from(fl).filter(f => /\.(jpg|jpeg|png)$/i.test(f.name));
    setFiles(arr);
  };
  const submit = (e) => {
    e.preventDefault();
    if (files.length < 20 || files.length > 60) return;
    onSubmit(files, markerCm);
  };
  return (
    <form onSubmit={submit} data-testid="recon3d-upload-form">
      <div className="rounded-xl p-6 mb-4 bg-white"
           style={{ border: '2px dashed #BCC5B5' }}>
        <input
          data-testid="recon3d-file-input"
          type="file"
          multiple
          accept=".jpg,.jpeg,.png"
          onChange={e => handleFiles(e.target.files)}
          className="block w-full text-sm"
        />
        <div className="mt-3 text-sm" style={{ color: '#4F4B44' }}>
          {files.length === 0 && 'Select 20–60 overlapping photos of a single plant/specimen.'}
          {files.length > 0 && (
            <span data-testid="recon3d-file-count">
              {files.length} image{files.length !== 1 ? 's' : ''} selected
              {files.length < 20 && <span className="text-red-700"> (need ≥20)</span>}
              {files.length > 60 && <span className="text-red-700"> (cap 60)</span>}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm" style={{ color: '#1B4332' }}>
          Calibration marker side (cm):
        </label>
        <input
          data-testid="recon3d-marker-cm"
          type="number"
          min={0.5}
          max={50}
          step={0.1}
          value={markerCm}
          onChange={e => setMarkerCm(parseFloat(e.target.value))}
          className="w-24 px-2 py-1 rounded border text-sm"
          style={{ borderColor: '#E5E5E0' }}
        />
        <span className="text-xs" style={{ color: '#4F4B44' }}>
          ArUco DICT_4X4_50 marker required for metric scaling.
        </span>
      </div>
      <button
        data-testid="recon3d-submit-job"
        type="submit"
        disabled={submitting || files.length < 20 || files.length > 60}
        className="px-4 py-2 rounded-lg font-medium text-sm text-white disabled:opacity-50"
        style={{ background: '#2D6A4F' }}
      >
        {submitting ? <><Loader2 size={14} className="inline animate-spin mr-2" /> Submitting…</>
          : 'Start sparse reconstruction'}
      </button>
      {error && <div className="mt-3 text-sm text-red-700" data-testid="recon3d-submit-error">{error}</div>}
    </form>
  );
}

// ──────────────────────────────────────────────────────────────
// Job status panel
// ──────────────────────────────────────────────────────────────
function JobStatus({ job, onDownloadPLY }) {
  if (!job) return null;
  const stageIcons = {
    queued: <Loader2 className="animate-spin" size={14} />,
    running: <Loader2 className="animate-spin" size={14} />,
    done: <CheckCircle2 size={14} style={{ color: '#2D6A4F' }} />,
    failed: <XCircle size={14} style={{ color: '#991B1B' }} />,
  };
  return (
    <div className="rounded-xl p-4 bg-white mb-4" style={{ border: '1px solid #E5E5E0' }}
         data-testid="recon3d-job-status">
      <div className="flex items-center gap-2 mb-3">
        {stageIcons[job.status]}
        <span className="font-semibold" style={{ color: '#1B4332' }}>
          Job {job.job_id?.slice(0, 8)}…  —  {job.status}
        </span>
        {job.current_stage && <span className="text-xs" style={{ color: '#4F4B44' }}>
          current: {job.current_stage}
        </span>}
      </div>
      {Array.isArray(job.stages) && job.stages.length > 0 && (
        <div className="text-xs font-mono space-y-0.5" data-testid="recon3d-stage-list">
          {job.stages.map((s, i) => (
            <div key={i} className="flex gap-2">
              <span>{s.ok ? '✓' : '✗'}</span>
              <span className="w-40">{s.name}</span>
              <span style={{ color: '#4F4B44' }}>
                {s.duration_s != null ? `${s.duration_s}s` : ''}
              </span>
              {s.message && <span className="text-red-700">{s.message}</span>}
            </div>
          ))}
        </div>
      )}
      {job.status === 'done' && job.result && (
        <div className="mt-3 text-sm space-y-1" data-testid="recon3d-job-result">
          <div>
            <b>Registered:</b> {job.result.n_registered_images}/{job.result.n_images}
            {' '}({Math.round((job.result.registration_rate || 0) * 100)}%)
          </div>
          <div><b>Sparse points:</b> {job.result.n_sparse_points?.toLocaleString()}</div>
          <div><b>Wall time:</b> {job.result.total_wall_time_s}s</div>
          <div>
            <b>Metric scale:</b>{' '}
            {job.result.scale_m_per_unit
              ? <span>{job.result.scale_m_per_unit.toFixed(5)} m/unit (from ArUco)</span>
              : <span style={{ color: '#92400E' }}>unavailable — {job.result.scale_info?.reason || 'no marker detected'}</span>}
          </div>
          <button
            data-testid="recon3d-download-ply"
            onClick={onDownloadPLY}
            className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded text-xs font-medium"
            style={{ background: '#1B4332', color: 'white' }}
          >
            <Download size={12} /> Download sparse .ply
          </button>
        </div>
      )}
      {job.status === 'failed' && (
        <div className="mt-2 text-sm text-red-700" data-testid="recon3d-job-error">
          Failed at <b>{job.failed_at}</b>: {job.error}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Traits panel
// ──────────────────────────────────────────────────────────────
function TraitsPanel({ job, info }) {
  if (!job?.result?.traits_metric) return (
    <div className="text-sm" style={{ color: '#4F4B44' }} data-testid="recon3d-traits-empty">
      Submit a job to see trait extraction.
    </div>
  );
  const t = job.result.traits_metric;
  const hasMetric = t.units === 'metres';
  const u = hasMetric ? 'm' : 'units';
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="recon3d-traits-panel">
      <div className="rounded-xl p-4 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: '#4F4B44' }}>
          Plant height
        </div>
        <div className="text-2xl font-semibold" style={{ color: '#1B4332' }} data-testid="recon3d-height">
          {t.height?.toFixed(3)} {u}
        </div>
      </div>
      <div className="rounded-xl p-4 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: '#4F4B44' }}>
          Canopy volume (convex hull)
        </div>
        <div className="text-2xl font-semibold" style={{ color: '#1B4332' }} data-testid="recon3d-volume">
          {t.convex_hull_volume != null ? t.convex_hull_volume.toFixed(6) : '—'} {hasMetric ? 'm³' : 'units³'}
        </div>
      </div>
      <div className="rounded-xl p-4 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: '#4F4B44' }}>
          Bounding box extents
        </div>
        <div className="text-sm font-mono" style={{ color: '#1B4332' }}>
          {t.extents?.map(e => e.toFixed(3)).join(' × ')} {u}
        </div>
      </div>
      <div className="rounded-xl p-4 bg-white" style={{ border: '1px solid #E5E5E0' }}>
        <div className="text-xs uppercase tracking-wide mb-1" style={{ color: '#4F4B44' }}>
          Point count
        </div>
        <div className="text-sm font-mono" style={{ color: '#1B4332' }}>
          {t.n_points?.toLocaleString()}
        </div>
      </div>
      {/* Disabled dense traits — honest banner */}
      <div className="md:col-span-2 rounded-xl p-4" data-testid="recon3d-dense-disabled-banner"
           style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
        <div className="flex gap-3 items-start">
          <AlertTriangle size={18} style={{ color: '#92400E', flexShrink: 0, marginTop: 2 }} />
          <div className="text-sm" style={{ color: '#78350F' }}>
            <b>Leaf-angle distribution</b> and <b>dense-surface biomass</b> require
            dense MVS reconstruction. {info?.banners?.dense_trait_disabled}
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Methodology collapsible — academic validation disclosure
// ──────────────────────────────────────────────────────────────
function MethodologyPanel({ data }) {
  if (!data) {
    return (
      <div className="rounded-xl p-4 mb-6 text-xs" data-testid="recon3d-methodology-loading"
           style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', color: '#6B7280' }}>
        Loading methodology…
      </div>
    );
  }
  const poses = data.ground_truth_camera_poses || [];
  const verts = data.ground_truth?.cube_vertices_m || [];
  const gt = data.ground_truth || {};
  return (
    <details className="rounded-xl mb-6 group" data-testid="recon3d-methodology-details"
             style={{ background: '#FFFFFF', border: '1px solid #E5E7EB' }}>
      <summary
        className="cursor-pointer select-none px-4 py-3 flex items-center gap-2 text-sm font-semibold rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
        data-testid="recon3d-methodology-summary"
        style={{ color: '#1F2937' }}
      >
        <BookOpen size={14} style={{ color: '#2D6A4F' }} />
        Methodology — how accuracy numbers above are measured
        <span className="ml-auto text-xs font-normal group-open:hidden"
              style={{ color: '#6B7280' }}>click to expand</span>
        <span className="ml-auto text-xs font-normal hidden group-open:inline"
              style={{ color: '#6B7280' }}>click to collapse</span>
      </summary>

      <div className="px-4 pb-4 pt-1 text-sm" style={{ color: '#374151' }}
           data-testid="recon3d-methodology-body">
        <p className="mb-3" style={{ color: '#4B5563' }}>
          <i>{data.one_liner}</i>
        </p>

        {/* Pipeline steps */}
        <div className="mb-4" data-testid="recon3d-methodology-steps">
          <div className="text-xs uppercase tracking-wide mb-1 font-semibold"
               style={{ color: '#6B7280' }}>Pipeline steps</div>
          <ol className="list-decimal ml-5 space-y-0.5 text-sm">
            {(data.pipeline_steps || []).map((s, i) => (
              <li key={i}>{s.replace(/^\d+\.\s*/, '')}</li>
            ))}
          </ol>
        </div>

        {/* Umeyama code */}
        <div className="mb-4" data-testid="recon3d-methodology-code">
          <div className="text-xs uppercase tracking-wide mb-1 font-semibold"
               style={{ color: '#6B7280' }}>
            Umeyama similarity alignment — actual code from regression suite
          </div>
          <pre className="rounded-lg p-3 text-xs overflow-x-auto font-mono"
               style={{ background: '#0B1220', color: '#D1E7DD', lineHeight: 1.5 }}>
            {data.umeyama_code}
          </pre>
        </div>

        {/* Ground truth summary */}
        <div className="mb-4" data-testid="recon3d-methodology-groundtruth">
          <div className="text-xs uppercase tracking-wide mb-1 font-semibold"
               style={{ color: '#6B7280' }}>Ground truth (metres)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-2">
            <div><b>Cube side</b>: {gt.cube_side_m} m</div>
            <div><b>Cube volume</b>: {gt.cube_volume_cm3} cm³</div>
            <div><b>ArUco side</b>: {gt.aruco_marker_side_m} m</div>
            <div><b>Cameras</b>: {gt.n_cameras} @ r={gt.camera_radius_m} m</div>
            <div><b>Elevations</b>: {(gt.camera_elevations_deg || []).join('°, ')}°</div>
            <div><b>Focal</b>: {gt.intrinsics?.focal_px} px</div>
            <div><b>Image</b>: {(gt.intrinsics?.image_size_px || []).join('×')} px</div>
            <div><b>Principal pt</b>: ({gt.intrinsics?.cx}, {gt.intrinsics?.cy})</div>
          </div>

          {/* Cube vertices */}
          <div className="text-xs font-semibold mt-3 mb-1" style={{ color: '#374151' }}>
            Cube vertices (8)
          </div>
          <div className="overflow-x-auto rounded border" style={{ borderColor: '#E5E7EB' }}>
            <table className="text-xs font-mono w-full" data-testid="recon3d-methodology-vertices">
              <thead style={{ background: '#F3F4F6' }}>
                <tr>
                  <th className="text-left px-2 py-1">vertex</th>
                  <th className="text-right px-2 py-1">x (m)</th>
                  <th className="text-right px-2 py-1">y (m)</th>
                  <th className="text-right px-2 py-1">z (m)</th>
                </tr>
              </thead>
              <tbody>
                {verts.map((v, i) => (
                  <tr key={i} style={{ background: i % 2 ? '#FAFAFA' : 'white' }}>
                    <td className="px-2 py-0.5">{v.vertex}</td>
                    <td className="px-2 py-0.5 text-right">{v.x_m.toFixed(3)}</td>
                    <td className="px-2 py-0.5 text-right">{v.y_m.toFixed(3)}</td>
                    <td className="px-2 py-0.5 text-right">{v.z_m.toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Camera poses */}
          <div className="text-xs font-semibold mt-3 mb-1" style={{ color: '#374151' }}>
            Camera centres — all {poses.length} ground-truth poses
          </div>
          <div className="overflow-x-auto rounded border max-h-64 overflow-y-auto"
               style={{ borderColor: '#E5E7EB' }}>
            <table className="text-xs font-mono w-full" data-testid="recon3d-methodology-poses">
              <thead style={{ background: '#F3F4F6', position: 'sticky', top: 0 }}>
                <tr>
                  <th className="text-left px-2 py-1">view</th>
                  <th className="text-right px-2 py-1">x (m)</th>
                  <th className="text-right px-2 py-1">y (m)</th>
                  <th className="text-right px-2 py-1">z (m)</th>
                </tr>
              </thead>
              <tbody>
                {poses.map((p, i) => (
                  <tr key={i} style={{ background: i % 2 ? '#FAFAFA' : 'white' }}>
                    <td className="px-2 py-0.5">{p.view}</td>
                    <td className="px-2 py-0.5 text-right">{p.x_m.toFixed(4)}</td>
                    <td className="px-2 py-0.5 text-right">{p.y_m.toFixed(4)}</td>
                    <td className="px-2 py-0.5 text-right">{p.z_m.toFixed(4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* References */}
        <div data-testid="recon3d-methodology-references">
          <div className="text-xs uppercase tracking-wide mb-1 font-semibold"
               style={{ color: '#6B7280' }}>References</div>
          <ul className="space-y-1.5 text-xs" style={{ color: '#374151' }}>
            {(data.references || []).map((r, i) => (
              <li key={i}>
                <b>{r.authors}</b> ({r.year}). <i>{r.title}</i>. {r.venue}.
                {r.doi && <> DOI:{' '}
                  <a href={`https://doi.org/${r.doi}`} target="_blank" rel="noreferrer"
                     style={{ color: '#2D6A4F', textDecoration: 'underline' }}>
                    {r.doi}
                  </a>.
                </>}
                {!r.doi && r.url && <> URL:{' '}
                  <a href={r.url} target="_blank" rel="noreferrer"
                     style={{ color: '#2D6A4F', textDecoration: 'underline' }}>{r.url}</a>.
                </>}
                {r.note && <span style={{ color: '#6B7280' }}> — {r.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </details>
  );
}

// ──────────────────────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────────────────────
export default function Reconstruction3D() {
  const [tab, setTab] = useState('upload');
  const [info, setInfo] = useState(null);
  const [methodology, setMethodology] = useState(null);
  const [job, setJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/info`).then(r => r.json()).then(setInfo).catch(() => {});
    fetch(`${API}/methodology`).then(r => r.json()).then(setMethodology).catch(() => {});
  }, []);

  useEffect(() => {
    if (!job?.job_id) return;
    if (['done', 'failed'].includes(job.status)) {
      clearInterval(pollRef.current);
      return;
    }
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API}/jobs/${job.job_id}`, { credentials: 'include' });
        if (r.ok) setJob(await r.json());
      } catch (e) {
        // Polling tick failed (transient network). Log + continue;
        // setInterval will retry on next tick.
        // eslint-disable-next-line no-console
        console.warn('Reconstruction3D polling failed:', e?.message || e);
      }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [job?.job_id, job?.status]);

  const submitJob = async (files, markerCm) => {
    setSubmitting(true); setSubmitErr(null);
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    try {
      const r = await fetch(`${API}/jobs?marker_side_cm=${markerCm}`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(d.detail || 'upload failed');
      }
      const created = await r.json();
      // Fetch full job record
      const jr = await fetch(`${API}/jobs/${created.job_id}`, { credentials: 'include' });
      setJob(await jr.json());
      setTab('pointcloud');
    } catch (e) {
      setSubmitErr(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPLY = async () => {
    if (!job?.job_id) return;
    const r = await fetch(`${API}/jobs/${job.job_id}/pointcloud.ply`, { credentials: 'include' });
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `recon_${job.job_id}.ply`;
    a.click();
  };

  const plyUrl = job?.status === 'done' ? `${API}/jobs/${job.job_id}/pointcloud.ply` : null;

  const TABS = [
    { id: 'upload', label: 'Upload & Calibrate', icon: Upload },
    { id: 'pointcloud', label: 'Point Cloud', icon: Box },
    { id: 'traits', label: '3D Trait Extraction', icon: Layers },
    { id: 'seed', label: '3D Seed / Fruit', icon: Sprout },
  ];

  return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }} data-testid="recon3d-page">
      <SharedNav />
      <div className="max-w-[1200px] mx-auto px-6 pt-8 pb-12">
        <div className="flex items-center gap-3 mb-2">
          <Box size={22} style={{ color: '#2D6A4F' }} strokeWidth={2.2} />
          <h1 className="font-display text-3xl" style={{ color: '#1B4332', letterSpacing: '-0.01em' }}>
            3D Reconstruction
          </h1>
          <span className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs"
                style={{ background: '#DCFCE7', color: '#14532D' }} data-testid="recon3d-live-badge">
            <CheckCircle2 size={11} /> Sparse pipeline live
          </span>
        </div>

        {/* Banner 1: Sparse-only honest disclosure */}
        <div className="rounded-xl p-4 mb-4" data-testid="recon3d-sparse-banner"
             style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
          <div className="flex gap-3 items-start">
            <AlertTriangle size={18} style={{ color: '#92400E', flexShrink: 0, marginTop: 2 }} />
            <div className="text-sm" style={{ color: '#78350F' }}>
              {info?.banners?.reconstruction_mode || (
                'Sparse reconstruction mode. Dense surface MVS requires a CUDA-enabled worker.'
              )}
            </div>
          </div>
        </div>

        {/* Validation honesty block — measured sparse SfM performance */}
        <div className="rounded-xl p-4 mb-6" data-testid="recon3d-validation-honesty"
             style={{ background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
          <div className="text-xs uppercase tracking-wide mb-2 font-semibold"
               style={{ color: '#374151' }}>
            Measured Sparse-SfM Accuracy (this deployment)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm"
               style={{ color: '#4B5563' }}>
            <div>
              <div className="text-xs" style={{ color: '#6B7280' }}>Synthetic cube (CPU SfM)</div>
              <div>Height error <b>~7 %</b>&nbsp;·&nbsp; Volume error <b>~8 %</b></div>
              <div className="text-xs mt-1">Regression target &lt;25 % / &lt;30 %</div>
            </div>
            <div>
              <div className="text-xs" style={{ color: '#6B7280' }}>Camera-pose RMS</div>
              <div><b>&lt; 1 mm</b> (inliers)&nbsp;·&nbsp;<b>&ge; 27/30</b> cameras registered</div>
              <div className="text-xs mt-1">Occasional outlier camera filtered</div>
            </div>
            <div>
              <div className="text-xs" style={{ color: '#6B7280' }}>Real photos (South Building)</div>
              <div><b>90 %</b> registration&nbsp;·&nbsp;<b>5 700</b> sparse points</div>
              <div className="text-xs mt-1">Non-metric path (no ArUco)</div>
            </div>
          </div>
          <div className="text-xs mt-3 pt-3 border-t" style={{ color: '#6B7280', borderColor: '#E5E7EB' }}>
            Validation datasets: <b>synthetic analytical cube</b> (algorithmic precision) +
            <b> COLMAP South Building</b> (real-world SfM, non-plant). For plant-specific
            validation, supply your own multi-view plant photo set with a calibration marker
            in view. ArUco metric-scale code is present in the pipeline but not regression-
            covered on synthetic data — real-world markers work normally.
          </div>
        </div>

        {/* Methodology collapsible (collapsed by default) */}
        <MethodologyPanel data={methodology} />

        {/* Tab nav */}
        <div className="flex gap-2 mb-5 border-b" style={{ borderColor: '#E5E5E0' }}
             data-testid="recon3d-tabs">
          {TABS.map(t => {
            const I = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`recon3d-tab-${t.id}`}
                className="px-3 py-2 text-sm font-medium -mb-px border-b-2"
                style={{
                  color: tab === t.id ? '#1B4332' : '#4F4B44',
                  borderColor: tab === t.id ? '#2D6A4F' : 'transparent',
                }}
              >
                <I size={14} className="inline mr-1.5" />{t.label}
              </button>
            );
          })}
        </div>

        {/* Job status always visible */}
        <JobStatus job={job} onDownloadPLY={downloadPLY} />

        {/* Tab contents */}
        {tab === 'upload' && (
          <UploadPanel onSubmit={submitJob} submitting={submitting} error={submitErr} />
        )}
        {tab === 'pointcloud' && (
          <div data-testid="recon3d-pointcloud-tab">
            {plyUrl ? (
              <div style={{ height: 520, background: '#F5F5F0', borderRadius: 12,
                            border: '1px solid #E5E5E0', overflow: 'hidden' }}>
                <PointCloudViewer plyUrl={plyUrl} />
              </div>
            ) : (
              <div className="rounded-xl p-8 bg-white text-center" style={{ border: '1px solid #E5E5E0' }}>
                <Eye size={32} style={{ color: '#BCC5B5' }} className="mx-auto mb-3" />
                <div className="text-sm" style={{ color: '#4F4B44' }}>
                  Submit a reconstruction job to view the sparse point cloud here.
                </div>
              </div>
            )}
          </div>
        )}
        {tab === 'traits' && (
          <TraitsPanel job={job} info={info} />
        )}
        {tab === 'seed' && (
          <div data-testid="recon3d-seed-tab">
            <div className="rounded-xl p-4 mb-4" data-testid="recon3d-seed-texture-banner"
                 style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
              <div className="flex gap-3 items-start">
                <AlertTriangle size={18} style={{ color: '#92400E', flexShrink: 0, marginTop: 2 }} />
                <div className="text-sm" style={{ color: '#78350F' }}>
                  {info?.banners?.seed_fruit_texture}
                </div>
              </div>
            </div>
            <UploadPanel onSubmit={submitJob} submitting={submitting} error={submitErr} />
          </div>
        )}
      </div>
    </div>
  );
}
